import { useEffect, useState, type ReactNode } from "react";
import classNames from "classnames";
import { Link, To, createPath, matchPath, useLocation, useResolvedPath } from "react-router-dom";

import { Box } from "ui/box";
import { ActionsMenu, MenuItem, SubmenuItem } from "ui/actions-menu";
import { Collapse } from "ui/collapse";

import { useSidebar } from "./sidebar-context";
import { useSidebarItemExpansion } from "./sidebar-items-context";

import { flattenItems } from "./sidebar-menu.utils";

import {
  getSidebarMenuItemClasses,
  SIDEBAR_MENU_ITEM_LABEL_SIZE_DEFAULT,
  SIDEBAR_MENU_ITEM_LABEL_SIZE_SM,
} from "./sidebar-menu.classes";
import { getSidebarMenuItemSx, type SidebarMenuItemSxOptions } from "./sidebar-menu-item.styles";
import { SidebarMenuItem as SidebarMenuItemType } from "./sidebar-menu.types";

import Icons from "assets";

export type SidebarMenuItemProps = {
  item: SidebarMenuItemType;
  activeItemId?: string;
  hasActiveItemIdMatch: boolean;
  onItemSelect?: (item: SidebarMenuItemType) => void;
  onNestedMenuOpenChange?: (itemId: string, isOpen: boolean) => void;
};

const isMenuItemToPathActive = (pathname: string, to: To) => {
  const path = typeof to === "string" ? to : createPath(to);

  return !!matchPath(
    {
      path,
      end: path === "/",
    },
    pathname,
  );
};

const isAnyDescendantActive = (
  items: SidebarMenuItemType[],
  pathname: string,
  activeItemId: string | undefined,
  hasActiveItemIdMatch: boolean,
) => {
  const descendants = flattenItems(items);

  if (hasActiveItemIdMatch) {
    return !!activeItemId && descendants.some((descendant) => descendant.id === activeItemId);
  }

  return descendants.some(
    (descendant) => descendant.to && isMenuItemToPathActive(pathname, descendant.to),
  );
};

const SidebarMenuItem = (props: SidebarMenuItemProps) => {
  if (props.item.actions?.length) {
    return <SidebarMenuActionsItem {...props} item={props.item} />;
  }

  if (props.item.children?.length) {
    return <SidebarMenuGroupItem {...props} item={props.item} />;
  }

  if (props.item.to) {
    return <SidebarMenuLinkItem {...props} item={props.item} />;
  }

  return <SidebarMenuButtonItem {...props} item={props.item} />;
};

const useSidebarMenuItemSx = (
  options: Omit<SidebarMenuItemSxOptions, "isCollapsed" | "isCompact">,
) => {
  const { itemLabelSize, isCollapsed } = useSidebar();

  return getSidebarMenuItemSx({
    ...options,
    isCollapsed,
    isCompact: itemLabelSize === "sm",
  });
};

const SidebarMenuActionsItem = ({
  item,
  activeItemId,
  hasActiveItemIdMatch,
  onItemSelect,
}: SidebarMenuItemProps) => {
  const { isCollapsed } = useSidebar();
  const { pathname } = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const actions = item.actions ?? [];

  const handleMenuOpen = () => setIsMenuOpen(true);
  const handleMenuClose = () => setIsMenuOpen(false);

  const isByIdActive = !!activeItemId && activeItemId === item.id;
  const isByPathActive = item.to ? isMenuItemToPathActive(pathname, item.to) : false;
  const isActive = hasActiveItemIdMatch ? isByIdActive : isByPathActive;

  const triggerSx = useSidebarMenuItemSx({
    isActive,
    hasFlyout: true,
    isFlyoutOpen: isMenuOpen,
  });

  const handleSelect = () => {
    onItemSelect?.(item);
    item.onSelect?.(item);
  };

  const trigger = item.to ? (
    <Box
      component={Link}
      to={item.to}
      onClick={handleSelect}
      sx={triggerSx}
      data-testid={item.testId}
      title={item.label}
      aria-current={isActive ? "page" : undefined}>
      <SidebarMenuItemContent item={item} isActionMenu={!isCollapsed} />
    </Box>
  ) : (
    <Box
      component="button"
      type="button"
      onClick={handleSelect}
      sx={triggerSx}
      data-testid={item.testId}
      title={item.label}
      aria-current={isActive ? "page" : undefined}>
      <SidebarMenuItemContent item={item} isActionMenu={!isCollapsed} />
    </Box>
  );

  return (
    <ActionsMenu
      itemName={item.label}
      open={isMenuOpen}
      onOpen={handleMenuOpen}
      onClose={handleMenuClose}
      placement="right-start"
      menuId={`sidebar-actions-menu-${item.id}`}
      trigger={trigger}>
      {actions.map((action) => (
        <MenuItem
          key={action.label}
          icon={action.icon}
          label={action.label}
          to={action.to}
          data-testid={action.testId}
          onClick={() => {
            action.onClick();
            setIsMenuOpen(false);
          }}
        />
      ))}
    </ActionsMenu>
  );
};

const CHEVRON_CLASSES = "size-4 text-gray-900";

const SidebarMenuItemContent = ({
  item,
  isExpanded,
  isCollapsible,
  isActionMenu,
}: {
  item: SidebarMenuItemType;
  isExpanded?: boolean;
  isCollapsible?: boolean;
  isActionMenu?: boolean;
}) => {
  const { isCollapsed, itemLabelSize } = useSidebar();
  const { icon, labelBase, labelExpanded, accessory } = getSidebarMenuItemClasses(
    itemLabelSize,
    isCollapsed,
  );
  const labelSizeClass =
    itemLabelSize === "sm" ? SIDEBAR_MENU_ITEM_LABEL_SIZE_SM : SIDEBAR_MENU_ITEM_LABEL_SIZE_DEFAULT;

  return (
    <>
      {item.icon && <span className={icon}>{item.icon}</span>}
      <span
        className={classNames(
          labelBase,
          !isCollapsed && labelSizeClass,
          !isCollapsed && labelExpanded,
          !isCollapsed && !item.icon && "ml-0",
        )}>
        {item.label}
      </span>
      {!isCollapsed && item.endIcon && <span className={accessory}>{item.endIcon}</span>}
      {!isCollapsed && (isCollapsible || isActionMenu) && (
        <span className={accessory}>
          {isCollapsible ? (
            isExpanded ? (
              <Icons.ChevronDown className={CHEVRON_CLASSES} />
            ) : (
              <Icons.ChevronRight className={CHEVRON_CLASSES} />
            )
          ) : (
            <Icons.ChevronRight className={CHEVRON_CLASSES} />
          )}
        </span>
      )}
    </>
  );
};

const SidebarMenuGroupItem = ({
  item,
  activeItemId,
  hasActiveItemIdMatch,
  onItemSelect,
  onNestedMenuOpenChange,
}: SidebarMenuItemProps) => {
  const { isCollapsed } = useSidebar();
  const { pathname } = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isExpanded, toggleExpanded } = useSidebarItemExpansion(item.id);
  const childItems = item.children ?? [];

  const handleMenuOpen = () => setIsMenuOpen(true);
  const handleMenuClose = () => setIsMenuOpen(false);

  const isActive = isAnyDescendantActive(childItems, pathname, activeItemId, hasActiveItemIdMatch);

  useEffect(() => {
    if (!isCollapsed) {
      setIsMenuOpen(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    onNestedMenuOpenChange?.(item.id, isCollapsed && isMenuOpen);

    return () => {
      onNestedMenuOpenChange?.(item.id, false);
    };
  }, [isCollapsed, isMenuOpen, item.id, onNestedMenuOpenChange]);

  const handleChildSelect = (childItem: SidebarMenuItemType) => {
    onItemSelect?.(childItem);
    childItem.onSelect?.(childItem);
  };

  const triggerSx = useSidebarMenuItemSx({
    isActive,
    // Only the collapsed trigger fills — expanded, the header's children carry the active row.
    hasFillStates: isCollapsed,
    hasFlyout: true,
    isFlyoutOpen: isMenuOpen,
  });

  if (!isCollapsed) {
    return (
      <div className="space-y-1">
        <Box
          component="button"
          type="button"
          sx={triggerSx}
          aria-expanded={isExpanded}
          onClick={toggleExpanded}
          title={item.label}>
          <SidebarMenuItemContent item={item} isExpanded={isExpanded} isCollapsible={true} />
        </Box>

        <Collapse in={isExpanded} unmountOnExit>
          <div className="space-y-1 pl-3">
            {childItems.map((childItem) => (
              <SidebarMenuItem
                key={childItem.id}
                item={childItem}
                activeItemId={activeItemId}
                hasActiveItemIdMatch={hasActiveItemIdMatch}
                onItemSelect={onItemSelect}
                onNestedMenuOpenChange={onNestedMenuOpenChange}
              />
            ))}
          </div>
        </Collapse>
      </div>
    );
  }

  return (
    <ActionsMenu
      itemName={item.label}
      open={isMenuOpen}
      onOpen={handleMenuOpen}
      onClose={handleMenuClose}
      placement="right-start"
      menuId={`sidebar-nested-menu-${item.id}`}
      trigger={
        <Box
          component="button"
          type="button"
          sx={triggerSx}
          data-testid={item.testId}
          title={item.label}
          aria-current={isActive ? "page" : undefined}>
          <SidebarMenuItemContent item={item} />
        </Box>
      }>
      {childItems.map(function renderFlyoutItem(childItem): ReactNode {
        if (childItem.children?.length) {
          return (
            <SubmenuItem
              key={childItem.id}
              icon={childItem.icon}
              label={childItem.label}
              isSelected={isAnyDescendantActive(
                childItem.children,
                pathname,
                activeItemId,
                hasActiveItemIdMatch,
              )}
              menuId={`sidebar-nested-menu-${childItem.id}`}
              data-testid={childItem.testId}>
              {childItem.children.map(renderFlyoutItem)}
            </SubmenuItem>
          );
        }

        const isChildByIdActive = !!activeItemId && activeItemId === childItem.id;
        const isChildByPathActive = childItem.to
          ? isMenuItemToPathActive(pathname, childItem.to)
          : false;
        const isChildActive = hasActiveItemIdMatch ? isChildByIdActive : isChildByPathActive;

        return (
          <MenuItem
            key={childItem.id}
            icon={childItem.icon}
            label={childItem.label}
            to={childItem.to}
            isSelected={isChildActive}
            data-testid={childItem.testId}
            onClick={() => {
              handleChildSelect(childItem);
              setIsMenuOpen(false);
            }}
          />
        );
      })}
    </ActionsMenu>
  );
};

const SidebarMenuButtonItem = ({
  item,
  activeItemId,
  hasActiveItemIdMatch,
  onItemSelect,
}: SidebarMenuItemProps) => {
  const isByIdActive = !!activeItemId && activeItemId === item.id;
  const isActive = hasActiveItemIdMatch ? isByIdActive : false;

  const itemSx = useSidebarMenuItemSx({ isActive });

  const handleSelect = () => {
    onItemSelect?.(item);
    item.onSelect?.(item);
  };

  return (
    <Box
      component="button"
      type="button"
      onClick={handleSelect}
      sx={itemSx}
      data-testid={item.testId}
      title={item.label}
      aria-current={isActive ? "page" : undefined}>
      <SidebarMenuItemContent item={item} />
    </Box>
  );
};

const SidebarMenuLinkItem = ({
  item,
  activeItemId,
  hasActiveItemIdMatch,
  onItemSelect,
}: SidebarMenuItemProps) => {
  const isByIdActive = !!activeItemId && activeItemId === item.id;

  const resolvedTo = useResolvedPath(item.to!);
  const { pathname } = useLocation();
  const isByPathActive = isMenuItemToPathActive(pathname, resolvedTo.pathname);
  const isActive = hasActiveItemIdMatch ? isByIdActive : isByPathActive;

  const itemSx = useSidebarMenuItemSx({ isActive });

  const handleSelect = () => {
    onItemSelect?.(item);
    item.onSelect?.(item);
  };

  return (
    <Box
      component={Link}
      to={item.to!}
      onClick={handleSelect}
      sx={itemSx}
      data-testid={item.testId}
      title={item.label}
      aria-current={isActive ? "page" : undefined}>
      <SidebarMenuItemContent item={item} />
    </Box>
  );
};

export default SidebarMenuItem;
